import { defaultThemeId, themes } from '@/lib/themes'
import { isStoryFontId } from '@/features/story-conversation/storyFonts'
import type { AppearancePreferences, StoryTextKind } from '@/types/preferences'

export const textKinds: StoryTextKind[] = ['normal', 'dialogue', 'italic', 'monospace']
export function defaultAppearance(): AppearancePreferences {
  return { version: 1, app: { themeId: defaultThemeId }, story: { fontId: 'default', themes: {} } }
}
export function normalizeStoryColor(value: unknown): string | null {
  if (typeof value !== 'string') return null
  const hex = value.trim().replace(/^#/, '')
  if (/^[\da-f]{3}$/i.test(hex)) return `#${[...hex].map(char => char + char).join('').toLowerCase()}`
  return /^[\da-f]{6}$/i.test(hex) ? `#${hex.toLowerCase()}` : null
}
function object(value: unknown): value is Record<string, unknown> {
  return !!value && typeof value === 'object' && !Array.isArray(value)
}
// Reject unsupported documents instead of silently replacing saved preferences with defaults.
export function parseAppearance(value: unknown): AppearancePreferences {
  if (!object(value) || value.version !== 1 || !object(value.app) || !object(value.story)
    || typeof value.app.themeId !== 'string' || !Object.hasOwn(themes, value.app.themeId)
    || !isStoryFontId(value.story.fontId) || !object(value.story.themes)) {
    throw new Error('These appearance settings are invalid or require a newer app version.')
  }
  const result = defaultAppearance()
  result.app.themeId = value.app.themeId
  result.story.fontId = value.story.fontId
  for (const [id, palette] of Object.entries(value.story.themes)) {
    if (!Object.hasOwn(themes, id) || !object(palette) || !object(palette.colors)) throw new Error('Invalid appearance palette.')
    const colors: AppearancePreferences['story']['themes'][string]['colors'] = {}
    for (const [kind, raw] of Object.entries(palette.colors)) {
      const color = normalizeStoryColor(raw)
      if (!textKinds.includes(kind as StoryTextKind) || !color) throw new Error('Use a hex color, such as #aabbcc.')
      colors[kind as StoryTextKind] = color
    }
    result.story.themes[id] = { colors }
  }
  return result
}
export function copyAppearance(value: AppearancePreferences): AppearancePreferences {
  return JSON.parse(JSON.stringify(value)) as AppearancePreferences
}
